/**
 * Settings › Providers and usage › Subscriptions (concept app-clarity `usage`): one row per
 * provider account with its 5-hour and weekly windows side by side (stacked on a phone), each with
 * % used, the reset time and how long until it, plus where the reading came from and how fresh it
 * is. A window turns amber at 85 %. The % used opens the account's details (every window, the
 * machines signed in to it, the raw reading).
 */
import { formatAgo } from '@viko/transcript-ui'
import { type ReactNode, useId, useState } from 'react'
import { type AccountUsageRow, windowLabel } from '../lib/account-usage.js'
import { resetCountdown } from '../lib/format.js'
import { accountStatus } from '../lib/provider-accounts.js'
import { Gauge } from './QuietMeter.js'

const PROVIDER = { claude: 'Claude', codex: 'Codex' } as const
const SOURCE = { claude: 'Anthropic', codex: 'OpenAI' } as const
const STALE_MS = 15 * 60_000
/** A window turns amber from here (Viko routes away at 90 %). */
export const USAGE_WARN_PCT = 85

/** "14:00 (in 1h 12m)" today, "Mon 09:00" further out. */
export function resetText(resetsAt: number, now: number): string {
  const d = new Date(resetsAt)
  const sameDay = new Date(now).toDateString() === d.toDateString()
  const clock = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (sameDay) return `resets ${clock} (${resetCountdown(resetsAt, now)})`
  const far = resetsAt - now > 6 * 86_400_000
  const day = d.toLocaleDateString(
    [],
    far ? { month: 'short', day: 'numeric' } : { weekday: 'short' },
  )
  return `resets ${day} ${clock} (${resetCountdown(resetsAt, now)})`
}

export function AccountUsageList({ rows, now }: { rows: AccountUsageRow[]; now: number }) {
  return (
    <ul className="st-subs" aria-label="Provider account usage">
      {rows.map((row) => (
        <AccountUsageItem key={row.usageKey} row={row} now={now} />
      ))}
    </ul>
  )
}

/** One window gauge: label, "62% used" (a button that opens the details), reset line. */
export function WindowGauge({
  label,
  usedPct,
  resetsAt,
  limit,
  now,
  meterLabel,
  onDrill,
  drillOpen,
  drillId,
  sub,
}: {
  label: string
  usedPct?: number
  resetsAt?: number
  limit?: boolean
  now: number
  meterLabel: string
  onDrill?: () => void
  drillOpen?: boolean
  drillId?: string
  sub?: ReactNode
}) {
  const pct = limit ? 100 : (usedPct ?? 0)
  const warn = limit || pct >= USAGE_WARN_PCT
  const text = limit
    ? 'limit reached'
    : usedPct === undefined
      ? 'not reported'
      : `${Math.round(pct)}% used`
  return (
    <Gauge
      label={label}
      value={
        onDrill ? (
          <button
            type="button"
            className={`st-inline-btn${warn ? ' st-wait' : ''}`}
            aria-expanded={drillOpen}
            {...(drillId ? { 'aria-controls': drillId } : {})}
            onClick={onDrill}
          >
            {text}
          </button>
        ) : (
          text
        )
      }
      pct={pct}
      meterLabel={meterLabel}
      {...(warn ? { wait: true } : {})}
      sub={sub ?? (resetsAt ? resetText(resetsAt, now) : 'reset not reported')}
    />
  )
}

function AccountUsageItem({ row, now }: { row: AccountUsageRow; now: number }) {
  const [open, setOpen] = useState(false)
  const detailsId = useId()
  const u = row.usage
  const name = u?.plan ?? PROVIDER[row.provider]
  const limit = u?.limitReached
  const windows = (u?.windows ?? []).filter((w) => w.usedPct !== undefined || w.resetsAt)
  const stale = u ? now - u.observedAt > STALE_MS : false
  const signedOut = row.aliases.every(
    (a) => a.account.mode === 'isolated' && a.account.readiness === 'login-required',
  )
  const balance = u?.credits
    ? u.credits.unlimited
      ? 'unlimited credits'
      : u.credits.balance
        ? `${u.credits.balance} credits left`
        : u.credits.hasCredits
          ? 'credits available'
          : 'no credits left'
    : undefined
  return (
    <li className="st-sub" aria-label={`${PROVIDER[row.provider]} · ${row.labels.join(' / ')}`}>
      <div className="st-sub__head">
        <span className="st-sub__name">
          {name}
          <span className="st-sub__label"> · {row.labels.join(' / ')}</span>
        </span>
        <span className="flex-1" />
        <span className={`st-sub__src${stale ? ' st-wait' : ''}`}>
          {u ? (
            <>
              from {SOURCE[row.provider]} · {formatAgo(u.observedAt, now)}
              {stale ? ' · may be out of date' : ''}
            </>
          ) : (
            'no reading yet'
          )}
          {signedOut ? ' · sign in required' : ''}
        </span>
      </div>
      {windows.length ? (
        <div className="st-sub__windows">
          {windows.map((w) => (
            <WindowGauge
              key={`${w.window}:${w.windowMinutes ?? ''}`}
              label={w.window === '5h' ? '5-hour window' : windowLabel(w)}
              {...(w.usedPct !== undefined ? { usedPct: w.usedPct } : {})}
              {...(w.resetsAt ? { resetsAt: w.resetsAt } : {})}
              limit={!!limit}
              now={now}
              meterLabel={`${name} ${windowLabel(w)} usage`}
              onDrill={() => setOpen((v) => !v)}
              drillOpen={open}
              drillId={detailsId}
              {...(limit?.resetsAt
                ? { sub: `limit reached · resumes ${resetCountdown(limit.resetsAt, now)}` }
                : {})}
            />
          ))}
        </div>
      ) : (
        <p className="st-sub__empty">
          {u
            ? 'No window reported in the last reading.'
            : row.provider === 'claude'
              ? 'Claude reports usage while a session runs; it appears after the next session on this account.'
              : 'Appears after the next session on this account.'}{' '}
          <button
            type="button"
            className="st-inline-btn"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={() => setOpen((v) => !v)}
          >
            Details
          </button>
        </p>
      )}
      {balance ? <p className="st-sub__empty">Credits and balance: {balance}</p> : null}
      {open ? (
        <div id={detailsId} className="st-sub__details">
          {u ? (
            <p className="m-0">
              Reading taken {new Date(u.observedAt).toLocaleString()}
              {row.reportedBy ? ` on ${row.reportedBy}` : ''}. A limit reading, not a charge.
            </p>
          ) : null}
          <span className="text-text-2">
            {row.aliases.length > 1
              ? 'Signed in on these machines (one shared quota, counted once):'
              : 'Signed in on:'}
          </span>
          <ul className="m-0 grid list-none gap-1 p-0">
            {row.aliases.map((a) => (
              <li key={`${a.machineId}:${a.account.id}`}>
                {a.machineName} · {a.account.label} — {accountStatus(a.account)}
                {a.account.loginPending ? ' · signing in' : ''}
                {a.account.activeRuns ? ` · ${a.account.activeRuns} running` : ''}
                <span className="block font-mono text-[14px] break-all text-text-2">
                  {a.account.id}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  )
}
